import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import StyledLink from 'components/StyledLink';
import { Chip, Spinner } from '@pearsontechnology/aero-components';
import { PageContent, PageActions } from 'components/PageStyles';
import AccountHelper from 'Workspace/helpers/AccountHelper';
import { featureKeys } from 'services/FeatureFlagService';
import ManualGradingComponentPopper from '../ManualGradingComponentPopper';
import { ACTIVITY } from '../../constants';
import messages from './messages';

import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  ScrollWrapper,
} from '../Grid/Table';
import AccountSummary from '../Grid/AccountSummary';
import CellValue from '../Grid/CellValue';
import {
  NOT_ATTEMPTED,
  UNSCORED,
  INCOMPLETE_ATTEMPT,
} from '../scoreReason';
import CSVDownloadButton from '../CSVDownloadButton';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';

const gradeCellDisplay = (score, progress) => {
  if (!score) {
    return '-';
  }
  if (score.reason === UNSCORED) {
    if (progress === null || progress === undefined || !progress.completion || progress.completion.value === 0) {
      return 'Unattempted';
    }
    if (progress.completion.value > 0 && progress.completion.value < 1) {
      return 'In progress';
    }
    // Only if their progress is 1 and the reason is still UNSCORED, show N/A.
    return 'N/A';
  }
  if (score.reason === NOT_ATTEMPTED) {
    return 'Unattempted';
  }
  if (score.reason === INCOMPLETE_ATTEMPT) {
    return '-';
  }
  return score.value;
};

function getActivityGrade(
  scoreByChildActivityId,
  activity,
  enrollment,
) {
  const allActivityProgress = scoreByChildActivityId[activity.id];
  if (!allActivityProgress || !allActivityProgress[enrollment.id]) {
    return { value: ' ' };
  }

  const scoreObject = allActivityProgress[enrollment.id];
  return {
    value: gradeCellDisplay(scoreObject.score, enrollment.progress),
    requiresGrading: scoreObject.requiresGrading,
    hasGradeables: scoreObject.hasGradeables,
  };
}

/**
 * Render a cell a based on whether or not manual grades exist, and the state
 * of those manual grades.
 */
function renderCell(enrollment, walkable, { value: computedGrade, requiresGrading, hasGradeables }, showPopper) {
  /* Truth table:
    *
    * | m. grades | any unattempted | any ungraded | All graded | score     | Cell value     | action |
    * |-----------|-----------------|--------------|------------|-----------|----------------|--------|
    * | -         | -               | -            | -          | unloaded  | ' '            | none   |
    * | false     | -               | -            | -          | true      | score value    | none   |
    * | false     | -               | -            | -          | false     | '—'            | none   |
    * | true      | true            | false        | false      | -         | score value    | none   |
    * | true      | -               | true         | false      | -         | 'GRADE'        | popper |
    * | true      | -               | false        | true       | -         | score value    | popper |
    */

  let grade = computedGrade;
  if (!grade && grade !== 0) {
    grade = '—';
  }

  // Get value to display in the cell
  const cellValue = (!requiresGrading) ?
    // set value to existing cell value
    (<CellValue>{grade}</CellValue>) :
    // set value to "GRADE"
    // TODO: i18l.
    (<Chip blue small>Grade</Chip>);

  // Get click action, if any (i.e., show popover?)
  const cellAction = (hasGradeables) ?
    // set popper
    {
      onClick: (evt) => showPopper(evt, walkable, enrollment),
      style: { cursor: 'pointer' },
    } :
    {};

  return (
    <TableCell key={`${enrollment.id}-${walkable.id}`} {...cellAction}>
      {cellValue}
    </TableCell>
  );
}

const NoTitle = 'No title';

function GradesGrid(props) {
  const { activity, childWalkables, enrollments, cohortId, deploymentId, workspaceId, navigationActions, scoresByChildWalkable, isLoading, getFeatureFlagValue } = props;
  const [popperState, setPopperState] = useState({ selectedEl: null, selectedChild: null, selectedStudent: null });
  const closePopper = () => setPopperState(({ selectedEl }) => ({ selectedEl, selectedChild: null, selectedStudent: null }));
  const showPopper = ({ currentTarget: selectedEl }, selectedChild, selectedStudent) => setPopperState({ selectedEl, selectedChild, selectedStudent });

  const tableColumns = useMemo(() => [
    'enrollments',
    activity.title,
    ...childWalkables.map((childWalkable) => (childWalkable.totalScore !== null && childWalkable.totalScore !== undefined)
      ? `${childWalkable.title} (${childWalkable.totalScore} pts)`
      : childWalkable.title
    ),
  ], [activity, childWalkables]);

  const tableData = useMemo(() => enrollments.map((enrollment) => [
    enrollment,
    {
      element: activity,
      score: gradeCellDisplay(enrollment.score, enrollment.progress),
    },
    ...childWalkables.map((childWalkable) => ({
      element: childWalkable,
      score: getActivityGrade(scoresByChildWalkable, childWalkable, enrollment).value,
    })),
  ]), [activity, childWalkables, enrollments, scoresByChildWalkable]);

  // This should always be false in this file
  const isNewDesignEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_GRADES_NEW_DESIGN);

  return (
    <React.Fragment>
      <PageActions style={{ overflowX: 'auto', paddingTop: 4, flex: isNewDesignEnabled ? 'none' : undefined }}>
        {navigationActions}
        <CSVDownloadButton
          label="Export to CSV"
          disabled={childWalkables.filter(({ id }) => !scoresByChildWalkable[id]).length > 0}
          fileName={`${activity.title}-grades`}
          columns={tableColumns}
          datas={tableData.map(([account, ...rows]) => ([
            AccountHelper.getFullName(account),
            ...rows.map(({ score }) => score),
          ]))}
        />
      </PageActions>
      <PageContent flex100={isNewDesignEnabled}>
        { isLoading && <Spinner />}
        { !isLoading && (
          <ScrollWrapper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell style={{ minWidth: 220, textAlign: 'left', paddingLeft: 16 }}>
                    {activity.title}
                  </TableHeaderCell>
                  <TableHeaderCell style={{ minWidth: 150 }}>Total</TableHeaderCell>
                  {childWalkables.map((childWalkable) => (
                    <TableHeaderCell
                      key={childWalkable.id}
                      style={{ minWidth: 150 }}
                    >
                      {childWalkable.elementType === ACTIVITY ? (
                        <StyledLink to={`/classes/${cohortId}/grades/${deploymentId}/grades/${childWalkable.id}`}>
                          {childWalkable.title || NoTitle}
                        </StyledLink>
                      ) : (
                        <span>{childWalkable.title || NoTitle}</span>
                      )}

                      {childWalkable.totalScore !== null && childWalkable.totalScore !== undefined && (
                        <React.Fragment>
                          {' '}
                          <span>
                            &lrm;&ndash; <FormattedMessage {...messages.numberOfPoints} values={{ num: childWalkable.totalScore }} />
                          </span>
                        </React.Fragment>
                      )}
                    </TableHeaderCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {enrollments.map((enrollment) => (
                  <TableRow key={enrollment.id}>
                    <TableHeaderCell style={{ minWidth: 220 }}>
                      <AccountSummary account={enrollment} />
                    </TableHeaderCell>
                    <TableCell style={{ minWidth: 80 }}>
                      <CellValue>
                        {gradeCellDisplay(enrollment.score, enrollment.progress)}
                      </CellValue>
                    </TableCell>

                    {childWalkables.map((childWalkable) => (
                      renderCell(
                        enrollment,
                        childWalkable,
                        getActivityGrade(scoresByChildWalkable, childWalkable, enrollment),
                        showPopper
                      )))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <ManualGradingComponentPopper
              el={popperState.selectedEl}
              show={!!(popperState.selectedChild || popperState.selectedStudent)}
              onClose={closePopper}
              cohortId={cohortId}
              deploymentId={deploymentId}
              walkableId={popperState.selectedChild && popperState.selectedChild.id}
              walkableType={popperState.selectedChild && popperState.selectedChild.elementType}
              studentId={popperState.selectedStudent && popperState.selectedStudent.id}
              workspaceId={workspaceId}
            />
          </ScrollWrapper>
        )}
      </PageContent>
    </React.Fragment>
  );
}

GradesGrid.propTypes = {
  isLoading: PropTypes.bool,
  activity: PropTypes.object,
  childWalkables: PropTypes.array,
  enrollments: PropTypes.array,
  scoresByChildWalkable: PropTypes.object,
  cohortId: PropTypes.string,
  workspaceId: PropTypes.string,
  deploymentId: PropTypes.string,
  navigationActions: PropTypes.element,
};

GradesGrid.defaultProps = {
  isLoading: true,
  activity: {},
  enrollments: [],
  childWalkables: [],
};

const flagNames = [featureKeys.WORKSPACE_GRADES_NEW_DESIGN];

export default enhanceWithFeatureFlagSubscription(GradesGrid, flagNames);
